import { InputLabel, MenuItem } from '@mui/material';
import { AxiosResponse } from 'axios';
import { memo } from 'react';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { HttpResponseList } from 'shell/models/http-response';
import { ItemVM } from 'shell/view-models/item';
import { PmiFormControl } from 'shell/components/shared/pmi-select/pmi-form-control';
import { PmiSelect } from 'shell/components/shared/pmi-select';
import { TipoOccupazioneLegittima } from 'shell/models/tipo-occupazione-legittima';
import { Operation } from 'shell/view-models/operation';
import { ResidenceID } from 'practice/constants/id-practice-edit-residence';

interface ListaOccupantiLegittimiProps {
  disabled?: boolean;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  label?: string;
  id?: string;
  typeResidence?: string;
}

// Range codici tipo occupazione legittima: 600-699 per la nuova residenza,
// 700-799 per la residenza in famiglia già esistente
const NEW_RESIDENCE_CODE_DA = 600;
const NEW_RESIDENCE_CODE_A = 699;
const EXISTING_FAMILY_CODE_DA = 700;
const EXISTING_FAMILY_CODE_A = 799;

const ListaOccupantiLegittimiComponent = ({
  disabled,
  required,
  onChange,
  value,
  label = 'Tipo occupazione legittima',
  error,
  id = 'type-occupation',
  typeResidence,
}: ListaOccupantiLegittimiProps): React.ReactElement => {
  let CODE_DA;
  let CODE_A;

  if (
    typeResidence &&
    (typeResidence === ResidenceID.new || typeResidence === ResidenceID.allFamily)
  ) {
    CODE_DA = NEW_RESIDENCE_CODE_DA;
    CODE_A = NEW_RESIDENCE_CODE_A;
  } else if (
    typeResidence &&
    (typeResidence === ResidenceID.family || typeResidence === ResidenceID.domesticPartnership)
  ) {
    CODE_DA = EXISTING_FAMILY_CODE_DA;
    CODE_A = EXISTING_FAMILY_CODE_A;
  }

  const [list] = useHttpFetch<HttpResponseList<TipoOccupazioneLegittima>, Array<ItemVM>>(
    `${__PRACTICE_API_URL__}/pratiche/tipo-occupazione-legittima/list?filters=code_Da:${CODE_DA}:${Operation.EQUALS}&filters=code_A:${CODE_A}:${Operation.EQUALS}`,
    {
      onBootstrap: Boolean(CODE_DA && CODE_A),
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: {},
      },
      transformer: (response: AxiosResponse<HttpResponseList<TipoOccupazioneLegittima>>) => {
        const { data } = response;
        return data?.payload.list?.map((item) => ({
          id: item.key?.idTipoOccupazioneLegittima?.toString() || '',
          value: item.descrizioneTipoOccupazioneLegittima || '',
        }));
      },
    }
  );

  const dataList = (list.data as Array<ItemVM>) || [];

  return (
    <PmiFormControl fullWidth>
      <InputLabel error={error} id={`${id}-label`}>
        {required ? `${label} *` : label || undefined}
      </InputLabel>
      <PmiSelect
        labelId={`${id}-label`}
        id={id}
        name={id}
        value={value}
        label={required ? `${label} *` : label || undefined}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        error={error}
      >
        <MenuItem value="">
          <em>Nessuno</em>
        </MenuItem>
        {dataList?.map((type: ItemVM) => (
          <MenuItem key={type.id} value={type.id}>
            {type.value}
          </MenuItem>
        ))}
      </PmiSelect>
    </PmiFormControl>
  );
};

ListaOccupantiLegittimiComponent.displayName = 'List occupanti legittimi component';

const ListaOccupantiLegittimi = memo(ListaOccupantiLegittimiComponent);

export default ListaOccupantiLegittimi as typeof ListaOccupantiLegittimiComponent;
